import { For, type JSX } from "solid-js";
import type { InlineOp, InlineKind } from "@fastdiff/worker";
import type { Token } from "@fastdiff/highlight";

export type DiffLineKind =
  | "equal"
  | "delete"
  | "insert"
  | "change-left"
  | "change-right"
  | "placeholder";

export interface SearchMatchRange {
  start: number;
  end: number;
  isCurrent: boolean;
}

interface DiffLineProps {
  lineNumber: number | null;
  kind: DiffLineKind;
  text: string;
  inlineOps?: InlineOp[];
  /** Per-line syntax-highlight tokens. */
  tokens?: ReadonlyArray<Token>;
  /** Per-line search match ranges (relative to text). */
  matches?: ReadonlyArray<SearchMatchRange>;
}

function markerForKind(kind: DiffLineKind): string {
  switch (kind) {
    case "delete":
    case "change-left":  return "−";
    case "insert":
    case "change-right": return "+";
    case "equal":        return " ";
    case "placeholder":  return " ";
  }
}

function visibleOps(kind: DiffLineKind, ops: InlineOp[]): InlineOp[] {
  const keep = (k: InlineKind): boolean => {
    if (kind === "change-left") return k === "equal" || k === "delete";
    if (kind === "change-right") return k === "equal" || k === "insert";
    return false;
  };
  return ops.filter((op) => keep(op.kind));
}

/** A base segment of text with one decoration class (or none). */
interface BaseSegment {
  start: number;
  end: number;
  className: string | null;
}

function tokenSegments(text: string, tokens: ReadonlyArray<Token>): BaseSegment[] {
  const out: BaseSegment[] = [];
  let cursor = 0;
  for (const t of tokens) {
    if (t.start < cursor) continue;
    if (t.start > cursor) out.push({ start: cursor, end: t.start, className: null });
    out.push({ start: t.start, end: t.end, className: `fd-hl-${t.scope}` });
    cursor = t.end;
  }
  if (cursor < text.length) out.push({ start: cursor, end: text.length, className: null });
  return out;
}

function inlineOpSegments(ops: InlineOp[]): BaseSegment[] {
  const out: BaseSegment[] = [];
  let cursor = 0;
  for (const op of ops) {
    const len = op.text.length;
    out.push({ start: cursor, end: cursor + len, className: `fd-inline-${op.kind}` });
    cursor += len;
  }
  return out;
}

function plainSegments(text: string): BaseSegment[] {
  if (text.length === 0) return [];
  return [{ start: 0, end: text.length, className: null }];
}

interface RenderPiece {
  start: number;
  end: number;
  className: string | null;
  match: SearchMatchRange | null;
}

function sliceByMatches(
  base: BaseSegment[],
  matches: ReadonlyArray<SearchMatchRange>,
): RenderPiece[] {
  if (matches.length === 0) {
    return base.map((s) => ({ ...s, match: null }));
  }
  const breaks = new Set<number>();
  for (const m of matches) { breaks.add(m.start); breaks.add(m.end); }
  const findMatchAt = (pos: number): SearchMatchRange | null => {
    for (const m of matches) {
      if (m.start <= pos && pos < m.end) return m;
    }
    return null;
  };
  const out: RenderPiece[] = [];
  for (const seg of base) {
    const cuts: number[] = [seg.start];
    for (const b of breaks) {
      if (b > seg.start && b < seg.end) cuts.push(b);
    }
    cuts.push(seg.end);
    cuts.sort((a, b) => a - b);
    for (let i = 0; i < cuts.length - 1; i++) {
      const s = cuts[i] as number;
      const e = cuts[i + 1] as number;
      if (s === e) continue;
      out.push({ start: s, end: e, className: seg.className, match: findMatchAt(s) });
    }
  }
  return out;
}

function mergeSegments(a: BaseSegment[], b: BaseSegment[]): BaseSegment[] {
  const breaks = new Set<number>();
  for (const s of a) { breaks.add(s.start); breaks.add(s.end); }
  for (const s of b) { breaks.add(s.start); breaks.add(s.end); }
  const sorted = [...breaks].sort((x, y) => x - y);
  const classAt = (segs: BaseSegment[], pos: number): string | null => {
    for (const s of segs) if (s.start <= pos && pos < s.end) return s.className;
    return null;
  };
  const out: BaseSegment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i] as number;
    const end = sorted[i + 1] as number;
    if (start === end) continue;
    const ca = classAt(a, start);
    const cb = classAt(b, start);
    const combined = [ca, cb].filter((c): c is string => c !== null).join(" ") || null;
    out.push({ start, end, className: combined });
  }
  return out;
}

function baseSegments(
  text: string,
  tokens: ReadonlyArray<Token> | undefined,
  inlineOps: InlineOp[] | null,
): BaseSegment[] {
  const hasTokens = tokens && tokens.length > 0;
  const hasInline = inlineOps && inlineOps.length > 0;
  if (hasTokens && hasInline) {
    return mergeSegments(inlineOpSegments(inlineOps), tokenSegments(text, tokens));
  }
  if (hasTokens) return tokenSegments(text, tokens);
  if (hasInline) return inlineOpSegments(inlineOps);
  return plainSegments(text);
}

function renderPiece(text: string, p: RenderPiece): JSX.Element {
  const slice = text.slice(p.start, p.end);
  return p.className ? <span class={p.className}>{slice}</span> : <>{slice}</>;
}

export function DiffLine(p: DiffLineProps) {
  const inlineOps = (): InlineOp[] | null =>
    (p.kind === "change-left" || p.kind === "change-right") && p.inlineOps
      ? visibleOps(p.kind, p.inlineOps)
      : null;

  const segments = (): BaseSegment[] =>
    baseSegments(p.text, p.tokens, inlineOps());

  const pieces = (): RenderPiece[] =>
    sliceByMatches(segments(), p.matches ?? []);

  // Group consecutive pieces sharing the same match (or no match) so each
  // match becomes ONE <mark> wrapping its inner spans.
  const groups = (): RenderPiece[][] => {
    const ps = pieces();
    const out: RenderPiece[][] = [];
    let cur: RenderPiece[] = [];
    let curMatch: SearchMatchRange | null = null;
    for (const x of ps) {
      if (x.match === curMatch) {
        cur.push(x);
      } else {
        if (cur.length > 0) out.push(cur);
        cur = [x];
        curMatch = x.match;
      }
    }
    if (cur.length > 0) out.push(cur);
    return out;
  };

  return (
    <div class={`fd-line fd-line-${p.kind}`}>
      <span class="fd-line-num">{p.lineNumber ?? ""}</span>
      <span class="fd-line-marker">{markerForKind(p.kind)}</span>
      <span class="fd-line-text">
        <For each={groups()}>
          {(grp) => {
            const m = grp[0]!.match;
            const inner = <For each={grp}>{(piece) => renderPiece(p.text, piece)}</For>;
            return m
              ? <mark class={`fd-search-match${m.isCurrent ? " fd-search-match-current" : ""}`}>{inner}</mark>
              : <>{inner}</>;
          }}
        </For>
      </span>
    </div>
  );
}
